import styled, { css, keyframes } from 'styled-components';

export const Container = styled.div`
  width: 100%;
`;

export const PageTitle = styled.h2`
  font-size: 22px;
  font-family: 'Roboto', sans-serif;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 4px 0;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 20px 0;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SearchInput = styled.input`
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  width: 280px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.35);
  }
`;

export const ViewToggle = styled.div`
  display: flex;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
`;

type ViewToggleButtonProps = {
  $isActive: boolean;
};

export const ViewToggleButton = styled.button<ViewToggleButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 34px;
  border: none;
  background: ${({ $isActive }) => $isActive ? 'var(--primary)' : 'transparent'};
  color: ${({ $isActive }) => $isActive ? '#fff' : 'rgba(0, 0, 0, 0.55)'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $isActive }) => $isActive ? 'var(--primary)' : 'rgba(0, 0, 0, 0.04)'};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const MonthSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const MonthButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: var(--primary);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const MonthLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  min-width: 140px;
  text-align: center;
  text-transform: capitalize;
`;

export const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

type FileCardProps = {
  $isExpired?: boolean;
  $isViewed?: boolean;
};

export const FileCard = styled.div<FileCardProps>`
  background: #fff;
  border-radius: 6px;
  padding: 12px 16px;
  border: 1px solid #f0f0f0;
  border-left: 3px solid ${({ $isExpired, $isViewed }) =>
    $isExpired ? 'var(--danger)' : $isViewed ? 'var(--success)' : 'var(--info)'};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

export const FileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileType = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: 8px;
`;

export const FileAmount = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
`;

export const FileMetaList = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const FileMeta = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

export const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

type StatusTagProps = {
  $color: string;
};

export const StatusTag = styled.span<StatusTagProps>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $color }) => `color-mix(in srgb, ${$color} 15%, transparent)`};
  color: ${({ $color }) => $color};

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: var(--primary);
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: color-mix(in srgb, var(--primary) 85%, #000);
    color: #fff;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

export const EmptyIcon = styled.div`
  font-size: 48px;
  color: rgba(0, 0, 0, 0.25);
  margin-bottom: 16px;
`;

export const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
  margin: 0;
`;

// Skeleton styles
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const SkeletonCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  border: 1px solid #f0f0f0;
  border-left: 4px solid #e8e8e8;
`;

export const SkeletonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

export const SkeletonBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const SkeletonMeta = styled.div`
  display: flex;
  gap: 24px;
`;

type SkeletonBoxProps = {
  $width?: number | string;
  $height?: number;
  $marginBottom?: number;
};

export const SkeletonBox = styled.div<SkeletonBoxProps>`
  width: ${({ $width }) => typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  height: ${({ $height }) => $height || 16}px;
  margin-bottom: ${({ $marginBottom }) => $marginBottom || 0}px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  border-radius: 4px;
`;

// Folder view styles
export const FoldersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

export const FolderCard = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

export const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;

export const FolderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const FolderInfo = styled.div`
  flex: 1;
`;

export const FolderName = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 2px 0;
`;

export const FolderCount = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

export const FolderArrow = styled.div`
  color: rgba(0, 0, 0, 0.25);

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Department section in list view
export const DepartmentSection = styled.div`
  margin-bottom: 24px;
`;

export const DepartmentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export const DepartmentTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
    color: var(--primary);
  }
`;

export const DepartmentCount = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 10px;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Section styles for splitting payable/informational
export const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    user-select: none;
    border-radius: 8px;
    padding: 8px;
    margin: -8px;
    margin-bottom: 8px;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }
  `}
`;

export const SectionIcon = styled.div<{ $color?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $color }) => $color ? `color-mix(in srgb, ${$color} 12%, transparent)` : 'color-mix(in srgb, var(--primary) 12%, transparent)'};
  color: ${({ $color }) => $color || 'var(--primary)'};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SectionInfo = styled.div`
  flex: 1;
`;

export const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin: 0 0 2px 0;
`;

export const SectionSubtitle = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

export const SectionBadge = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: ${({ $color }) => $color ? `color-mix(in srgb, ${$color} 15%, transparent)` : '#f0f0f0'};
  color: ${({ $color }) => $color || 'rgba(0, 0, 0, 0.65)'};
`;

export const SectionToggle = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SectionContent = styled.div<{ $isOpen?: boolean }>`
  overflow: hidden;
  max-height: ${({ $isOpen }) => $isOpen ? '2000px' : '0'};
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  transition: max-height 0.3s ease, opacity 0.2s ease;
`;
